import React, { Component, ReactNode } from "react";
import { Icon, MenuItem, Tooltip, TooltipProps } from "@material-ui/core";
import styled from "styled-components";

import { Gap5 } from "js/react/components/Gap";
import { ThumbnailCard, thumbnailMenuButtonStyles } from "js/react/views/AddSlide/Panes/Components/ThumbnailCard";
import { Thumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import Thumbnails from "js/core/models/thumbnails";
import { IconMenu } from "js/react/components/UiComponents";
import { THUMBNAIL_SIZES } from "common/constants";
import getLogger, { LogGroup } from "js/core/logger";
import getUserProfile from "js/core/services/userProfiles";
import WorkspaceController from "js/controllers/WorkspaceController";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { Divider } from "js/Components/Divider";
import { t } from "js/i18n";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { getStaticUrl } from "js/config";
import type { SharedSlidesItem } from "js/controllers/SharedSlidesPaneController";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";

const logger = getLogger(LogGroup.THUMBNAIL);

const arrowShapeUpUrl = getStaticUrl("/images/ui/ornaments/arrow-shape-up.svg");

export type SharedSlideThumbnailProps = {
    item: SharedSlidesItem;
    firebaseUser: { uid: string };
    highQuality?: boolean;
    showThemeThumbnails?: boolean;
    selected?: boolean;
    isManageLibrary?: boolean;
    canUpgradeToManageSharedResources?: boolean;
    folderId?: string;
    onDoubleClick?: (item: SharedSlidesItem) => void;
    onEditSlide?: (item: SharedSlidesItem) => void;
    onDeleteSlide?: (item: SharedSlidesItem) => void;
    onRemoveFromFolder?: (item: SharedSlidesItem) => void;
    onDuplicateSlide?: (item: SharedSlidesItem) => void;
    onMouseDown?: React.MouseEventHandler;
    onMouseUp?: React.MouseEventHandler;
    onContextMenu?: React.MouseEventHandler;
    onVisibleChange?: (isVisible: boolean, item: SharedSlidesItem) => void;
};

type SharedSlideThumbnailState = {
    url: string;
    isVisible: boolean;
    isHovering: boolean;
    itemInfo: ReactNode | null;
};

const ArrowShapeUp = styled.div.attrs<{
    $fill?: string;
    $width?: number;
    $height?: number;
    $iconUrl: string;
}>(({ $fill, $width = 15, $height = 15 }) => ({
    style: {
        width: `${$width}px`,
        height: `${$height}px`,
        backgroundColor: $fill,
    }
}))<{
    $fill?: string;
    $width?: number;
    $height?: number;
    $iconUrl: string;
}>`
    display: inline-block;
    -webkit-mask: ${({ $iconUrl }) => `url("${$iconUrl}")`} center / contain no-repeat;
    mask: ${({ $iconUrl }) => `url("${$iconUrl}")`} center / contain no-repeat;
`;

const ThumbnailBar = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    label {
        flex-grow: 2;
        text-align: left;
        margin: 0px 10px 0px 0px;
    }
    .MuiIcon-root {
        margin-right: 3px;
        font-size: 18px;
    }
    & > div {
        font-size: 12px;
    }
    button {
        padding: 2px;
    }
`;

const ThumbnailContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    padding: 9px 10px 10px 10px;
`;

const ThumbnailMetaRow = styled.div`
    display: flex;
    align-items: center;
    font-size: 10px;
    line-height: 19px;
    color: #666;
`;

const PresentationCountRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    line-height: 16px;

    .MuiIcon-root {
        font-size: 16px;
        color: #222;
    }
`;

const ThumbnailMenuWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StyledIconMenu = styled(IconMenu)`
    &&& {
        ${thumbnailMenuButtonStyles}
    }
`;

const LikeButtonWrap = styled.div`
    ${thumbnailMenuButtonStyles}
    display: flex;
    align-items: center;
    gap: 4px;
    line-height: 125%;
    letter-spacing: -0.32px;
    cursor: pointer;
    color: #666;
`;

const DisabledSlideIcon = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 30px;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        color: white;
        font-size: 20px;
    }
`;

const SharedSlideTooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 170px;
    padding: 6px 10px;
`;

const SharedSlideTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))`
    & .MuiTooltip-tooltip {
        padding: 0;
    }
`;

const SharedSlideTooltipText = styled.div`
    color: #FFF;
    font-family: "Source Sans Pro", sans-serif;
    font-size: 11px;
    font-style: normal;
    line-height: 125%;
`;

const SharedSlideTooltipTitle = styled(SharedSlideTooltipText)`
    font-weight: 600;
`;

const SharedSlideTooltipDescription = styled(SharedSlideTooltipText)`
    font-weight: 400;
`;

const SharedSlideLinkBadge = styled.div`
    background: #FF4500;
    border-radius: 50%;
    display: flex;
`;

const SharedSlideLinkBadgeSpacer = styled.div`
    visibility: hidden;
    pointer-events: none;
`;

const StyledIcon = styled(Icon)`
    &&& {
    color: white;
    margin: 0;
    padding: 2;
    font-size: 20px;
    transform: scale(0.75);

    }
`;

function SharedSlideLinkBadgeIndicator({ isTemplate }: { isTemplate: boolean }) {
    const badge = (
        <SharedSlideLinkBadge>
            <StyledIcon fontSize="small">link</StyledIcon>
        </SharedSlideLinkBadge>
    );

    if (isTemplate) {
        return (
            <SharedSlideLinkBadgeSpacer aria-hidden>
                {badge}
            </SharedSlideLinkBadgeSpacer>
        );
    }

    return (
        <SharedSlideTooltip
            title={(
                <SharedSlideTooltipContent>
                    <SharedSlideTooltipTitle>{t("Shared Slide")}</SharedSlideTooltipTitle>
                    <SharedSlideTooltipDescription>
                        {t("Only Librarians and Owners can edit. Changes apply everywhere this slide is used.")}
                    </SharedSlideTooltipDescription>
                </SharedSlideTooltipContent>
            )}
        >
            {badge}
        </SharedSlideTooltip>
    );
}

class SharedSlideThumbnailBase extends Component<SharedSlideThumbnailProps, SharedSlideThumbnailState> {
    ref = React.createRef<HTMLDivElement>();
    observer?: IntersectionObserver;
    isUnmounted = false;

    state: SharedSlideThumbnailState = {
        url: "",
        isVisible: false,
        isHovering: false,
        itemInfo: null,
    };

    componentDidMount() {
        void this.loadItemInfo();

        this.observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                this.observer?.disconnect();
                this.setState({ isVisible: true });
            }
        });

        if (this.ref.current) {
            this.observer.observe(this.ref.current);
        }
    }

    componentDidUpdate(prevProps: SharedSlideThumbnailProps, prevState: SharedSlideThumbnailState) {
        const { item, onVisibleChange } = this.props;
        const { isVisible } = this.state;

        if (prevState.isVisible !== isVisible) {
            onVisibleChange?.(isVisible, item);
        }

        if (
            (prevState.isVisible !== isVisible && isVisible) ||
            prevProps.item.id !== item.id ||
            prevProps.item.contentModifiedAt !== item.contentModifiedAt
        ) {
            void this.loadThumbnail()
                .catch(err => logger.error(err, "[SharedSlideThumbnail] loadThumbnail() failed", { id: item.id }));
        }

        if (item.description !== prevProps.item.description) {
            void this.loadItemInfo();
        }
    }

    componentWillUnmount() {
        this.isUnmounted = true;
        this.observer?.disconnect();
    }

    async loadThumbnail() {
        const { item, highQuality, showThemeThumbnails = true } = this.props;

        this.setState({ url: "" });

        let url = "";

        const presentation = PresentationEditorController.presentation;
        if (showThemeThumbnails && presentation && !presentation.get("isDummy")) {
            const presentationId = presentation.id;
            const firstSlideModifiedAt = presentation.get("firstSlideModifiedAt");
            url = await Thumbnails.getSignedUrlAndLoad(
                item.id,
                firstSlideModifiedAt,
                presentationId,
                THUMBNAIL_SIZES[highQuality ? "large" : "small"].suffix
            );
        } else {
            url = await Thumbnails.getSignedUrlAndLoad(item.id, item.contentModifiedAt);
        }

        if (!this.isUnmounted) {
            this.setState({ url });
        }
    }

    handleLikeItem = async () => {
        const { item: { resource }, firebaseUser } = this.props;

        let ratedByUsers = resource.metadata?.ratedByUsers ?? [];
        if (!ratedByUsers.includes(firebaseUser.uid)) {
            ratedByUsers.push(firebaseUser.uid);
        } else {
            ratedByUsers = ratedByUsers.filter(uid => uid !== firebaseUser.uid);
        }

        try {
            await WorkspaceController.updateWorkspaceResource({
                resourceId: resource.resourceId,
                resourceType: resource.resourceType,
                metadata: {
                    ratedByUsers
                }
            });
        } catch (err) {
            logger.error(err, "[SharedSlideThumbnail] handleLikeItem() failed", { id: resource.resourceId });
        }
    };

    async loadItemInfo() {
        const { item } = this.props;

        try {
            const profile = await getUserProfile(item.createdBy);

            this.setState({
                itemInfo: <div>{t("Created by")} {profile.displayName || profile.email || t("Unknown")}</div>
            });
        } catch (err) {
            logger.error(err, "[SharedSlideThumbnail] loadItemInfo() failed", { id: item.id });
        }
    }

    render() {
        const {
            item,
            onDoubleClick,
            selected,
            isManageLibrary,
            canUpgradeToManageSharedResources,
            folderId,
            onEditSlide,
            onDeleteSlide,
            onRemoveFromFolder,
            onDuplicateSlide,
            onMouseDown,
            onMouseUp,
            onContextMenu,
        } = this.props;
        const { url, itemInfo, isHovering } = this.state;

        const clickProps = isManageLibrary
            ? { onDoubleClick }
            : {
                onDoubleClick,
                ...(onMouseDown ? { onMouseDown } : { onClick: onDoubleClick }),
            };

        const { isTemplate } = item;

        return (
            <ThumbnailCard
                ref={this.ref}
                data-item-id={item.id}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                selected={selected}
                onContextMenu={onContextMenu}
                onMouseEnter={() => this.setState({ isHovering: true })}
                onMouseLeave={() => this.setState({ isHovering: false })}
                menuOverlay={isHovering && isManageLibrary && (
                    <ThumbnailMenuWrap>
                        <Tooltip title={t("Upvote template to let your team know it's useful")}>
                            <LikeButtonWrap onClick={this.handleLikeItem}>
                                <ArrowShapeUp $fill="#666" $width={16} $height={16} $iconUrl={arrowShapeUpUrl} />
                                <div>{item.ratedByCount}</div>
                            </LikeButtonWrap>
                        </Tooltip>
                        <StyledIconMenu handleCloseMenu={() => this.setState({ isHovering: false })} fontSize="medium">
                            {canUpgradeToManageSharedResources ? [
                                <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Slide...")}</MenuItem>,
                                <MenuItem key="properties" disabled divider><UpgradeIcon /><Gap5 />{t("View Properties...")}</MenuItem>,
                                <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate")}</MenuItem>,
                            ] : [
                                <MenuItem key="edit" divider onClick={() => onEditSlide?.(item)}><Icon>edit</Icon>{t("Edit Slide...")}</MenuItem>,
                                <MenuItem key="properties" divider onClick={() => onDoubleClick?.(item)}><Icon>label</Icon>{t("View Properties...")}</MenuItem>,
                                <MenuItem key="duplicate" divider onClick={() => onDuplicateSlide?.(item)}><Icon>file_copy</Icon>{t("Duplicate")}</MenuItem>,
                            ]}
                            {folderId && onRemoveFromFolder && (
                                <MenuItem key="remove-from-folder" divider onClick={() => onRemoveFromFolder(item)}>
                                    <Icon>remove_circle</Icon>{t("Remove from Folder")}
                                </MenuItem>
                            )}
                            <MenuItem key="delete" onClick={() => onDeleteSlide?.(item)}><Icon>delete</Icon>{t("Delete")}</MenuItem>
                        </StyledIconMenu>
                    </ThumbnailMenuWrap>
                )}
                barVariant="custom"
                barContent={(
                    <ThumbnailContent>
                        <ThumbnailBar>
                            <label>{item.name}</label>
                            <SharedSlideLinkBadgeIndicator isTemplate={isTemplate} />
                        </ThumbnailBar>
                        <ThumbnailMetaRow>
                            {itemInfo && <span>{itemInfo}</span>}
                            {itemInfo && !isTemplate && <Divider vertical margin={8} style={{ background: "#DDD", borderRadius: "100px" }} />}
                            {!isTemplate && (
                                <PresentationCountRow>
                                    <Icon>cloud_download</Icon>
                                    <div>{item.presentationCount}</div>
                                </PresentationCountRow>
                            )}

                        </ThumbnailMetaRow>
                    </ThumbnailContent>
                )}
            >
                <Thumbnail
                    readOnly
                    {...clickProps}
                    url={url}
                    showSpinner
                    style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                    imageStyle={item.isDisabled ? { filter: "brightness(0.7)" } : {}}
                >
                    {item.isDisabled && (
                        <DisabledSlideIcon>
                            <Icon>visibility_off</Icon>
                        </DisabledSlideIcon>
                    )}
                </Thumbnail>
            </ThumbnailCard>
        );
    }
}

export const SharedSlideThumbnail = withFirebaseUser(SharedSlideThumbnailBase);
